import {
    Button, Grid, IconButton, Input, Typography
} from '@mui/material';
import { useTheme } from '@mui/styles';
import { ActionButton } from '@tassat/button';
import { CaretLeft as LeftCaretIcon, CaretRight as RightCaretIcon } from '@tassat/icon';
import { muiTheme } from '@tassat/payments-themes';
import { convertToNumber } from '@tassat/utility';
import * as React from 'react';

import muiStyles from './styles';
import { IPageSelectorProps, PageConfig } from './types';

const PageSelector: React.FC<IPageSelectorProps> = (props) => {
    const {
        data,
        totalRows,
        recordsPerPage,
        currentPageNum,
        disabled,
        changePageOnApplyOnly = false,
        onPageChange
    } = props;

    const MAX_PER_PAGE = recordsPerPage || 15;

    const theme = useTheme<typeof muiTheme>();
    const classes = muiStyles(props);

    const [ pageInputValue, setPageInputValue ] = React.useState<string>('');
    const numberOfPages = Math.ceil((totalRows || (data || []).length) / MAX_PER_PAGE);
    const MAX_PAGE_TO_SHOW = numberOfPages - 1;
    const buttons: Array<React.JSX.Element> = [];
    const prevCurrentPageNum = React.useRef(currentPageNum);

    React.useEffect(() => {
        if (changePageOnApplyOnly && prevCurrentPageNum.current !== currentPageNum) {
            setPageInputValue('');
        }
        prevCurrentPageNum.current = currentPageNum;
    }, [ currentPageNum, changePageOnApplyOnly ]);

    const submitPageChange = (page: number) => {
        if (Number.isNaN(page) || page > numberOfPages || page < 1 || page === currentPageNum) {
            return;
        }

        onPageChange(page);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (disabled) {
            return;
        }
        setPageInputValue(e.target.value);
    };

    const handlePageChange = (e: React.FocusEvent<HTMLInputElement>) => {
        if (disabled) {
            return;
        }

        const value = convertToNumber(e?.target.value, true);

        if (!Number.isNaN(value) && value <= numberOfPages && value >= 1) {
            setPageInputValue(String(value));
            if (!changePageOnApplyOnly) {
                submitPageChange(value);
            }
        } else {
            setPageInputValue('');
        }
    };

    // Only display a subset of page numbers
    let low = PageConfig.MIN_PAGE_TO_SHOW;
    let high = PageConfig.PAGES_TO_RIGHT;
    let renderLeftEllipsis = true;
    let renderRightEllipsis = true;

    if (numberOfPages <= PageConfig.MIN_PAGE_THRESHOLD) {
        high = MAX_PAGE_TO_SHOW;
        renderLeftEllipsis = false;
        renderRightEllipsis = false;
    } else if (currentPageNum === 1) {
        renderLeftEllipsis = false;
        high = currentPageNum + PageConfig.PAGES_TO_RIGHT;
    } else if (currentPageNum === numberOfPages || currentPageNum + PageConfig.PAGES_TO_RIGHT >= numberOfPages) {
        low = currentPageNum - PageConfig.PAGES_TO_LEFT;
        high = MAX_PAGE_TO_SHOW;
        renderRightEllipsis = false;
        // Extra 1 to deal with first page (below)
    } else if (currentPageNum - PageConfig.PAGES_TO_LEFT - 1 <= 0 &&
        currentPageNum + PageConfig.PAGES_TO_RIGHT >= MAX_PAGE_TO_SHOW) {
        high = MAX_PAGE_TO_SHOW;
        renderLeftEllipsis = false;
    } else if (currentPageNum - PageConfig.PAGES_TO_LEFT - 1 <= 0) {
        high = currentPageNum + PageConfig.PAGES_TO_RIGHT;
        renderLeftEllipsis = false;
    } else {
        low = currentPageNum - PageConfig.PAGES_TO_LEFT;
        high = currentPageNum + PageConfig.PAGES_TO_RIGHT;
    }

    for (let i = low; i <= high; i += 1) {
        buttons.push(
            <Button
                className={`${classes.pageButton} ${currentPageNum === i && classes.pageSelected}`}
                disabled={disabled}
                key={`page-${i}`}
                onClick={() => onPageChange(i)}
            >
                {i}
            </Button>
        );
    }

    return (
        <Grid
            alignItems='center'
            className={classes.root}
            container
            direction='row'
            spacing={0}
            wrap='nowrap'
        >
            <Grid
                className={classes.pageButtonContainer}
                item
            >
                <IconButton
                    aria-label='left caret'
                    className={classes.pageButton}
                    disabled={disabled}
                    key='left-caret'
                    onClick={() => currentPageNum !== 1 && onPageChange(currentPageNum - 1)}
                >
                    <LeftCaretIcon
                        className={classes.caretIcon}
                        iconColor={theme?.app?.clientTheme.colors.primaryColor || '#375073'}
                    />
                </IconButton>
                <Button
                    className={`${classes.pageButton} ${currentPageNum === 1 && classes.pageSelected}`}
                    disabled={disabled}
                    key='page-00'
                    onClick={() => onPageChange(1)}
                >
                    1
                </Button>
                {renderLeftEllipsis && '...'}
                {buttons}
                {renderRightEllipsis && '...'}
                {numberOfPages > 1 &&
                    (
                    <Button
                        className={`${classes.pageButton} ${currentPageNum === numberOfPages && classes.pageSelected}`}
                        disabled={disabled}
                        key={`page-${numberOfPages}`}
                        onClick={() => onPageChange(numberOfPages)}
                    >
                        {numberOfPages}
                    </Button>
                )}
                <IconButton
                    aria-label='right caret'
                    className={classes.pageButton}
                    disabled={disabled}
                    key='right-caret'
                    onClick={() => currentPageNum !== numberOfPages && onPageChange(currentPageNum + 1)}
                >
                    <RightCaretIcon
                        className={classes.caretIcon}
                        iconColor={theme?.app?.clientTheme.colors.primaryColor || '#375073'}
                    />
                </IconButton>
            </Grid>
            <Grid
                className={classes.goToContainer}
                container
                direction='row'
                item
                spacing={0}
            >
                <Typography
                    className={classes.goToLabel}
                    variant='body2'
                >Go to page
                </Typography>
                <Input
                    classes={{ input: classes.input }}
                    className={classes.goToInput}
                    disabled={disabled}
                    inputProps={{
                        step: 1,
                        min: 1
                    }}
                    onBlur={handlePageChange}
                    onChange={handleInputChange}
                    type='number'
                    value={pageInputValue}
                />
                {changePageOnApplyOnly &&
                    (
                    <ActionButton
                        className={classes.applyButton}
                        disabled={disabled}
                        handleClick={() => submitPageChange(convertToNumber(pageInputValue, true))}
                        text='Apply'
                        type='primary'
                    />
                )}
            </Grid>
        </Grid>
    );
};

export default PageSelector;